import showdown from 'showdown';
import { useRouter } from 'next/router';
import ReactDOMServer from 'react-dom/server';
import React, { useState, useEffect } from 'react';

import './SurveyFullForm.scss';
import { getString } from '@helpers/styles';
import icons from '../../../shared/assets/icons/redesign';
import { openEmailClientWithFormData, prefillSurveyForm } from '@helpers/form';

const { ChevronLeft, ChevronDown, Check } = icons;

const converter = new showdown.Converter();

export default function SurveyForm({
  user,
  formAnswer,
  initialLocale,
  preview,
  returnTo,
  alternativeView,
  updateFormAnswer,
  submitFormAnswer,
  showMessagePopup
}) {
  const { xuid, config, form, data } = formAnswer || {};
  const router = useRouter();
  const [currentPage, setCurrentPage] = useState(1);
  const [completed, setCompleted] = useState(false);

  const [Survey, setSurvey] = useState(null);
  const [model, setModel] = useState(null);
  const [isLastPage, setIsLastPage] = useState(false);
  const [locales, setLocales] = useState([]);
  const [activeLocale, setActiveLocale] = useState('');

  const pageCount = getPageCount(model);

  let parsed = {};
  try {
    parsed = JSON.parse(config || '{}');
  } catch (e) {}

  const customHTML = parsed.completedHtml?.nl || parsed.completedHtml;

  function isSurveyCompleted(m) {
    if (!m) return false;

    return (
      m.isCompleted === true ||
      m.isCompletedBefore === true ||
      m.state === 'completed' ||
      m.state === 'completedbefore' ||
      m.state === 'completedBefore'
    );
  }

  async function onComplete(survey) {
    setCompleted(true);

    const progress = document.querySelector('.surveyForm-progress-bar');
    const nav = document.querySelector('.surveyForm-navigation-bar');
    const prev = document.querySelector('.surveyForm-prev-page-button');

    if (progress) progress.style.display = 'none';
    if (nav) nav.style.display = 'none';
    if (prev) prev.style.display = 'none';

    const header = document.querySelector('.sv_header');
    if (header) header.style.display = 'none';

    window.scrollTo(0, 0);

    if (form?.xuid) {
      openEmailClientWithFormData(form.xuid, survey.data);
    }

    if (updateFormAnswer)
      await updateFormAnswer({
        variables: {
          formAnswerXuid: xuid,
          data: JSON.stringify(survey.data)
        }
      });

    if (submitFormAnswer) {
      submitFormAnswer({ variables: { formAnswerXuid: xuid } });
    }
  }

  useEffect(() => {
    let isMounted = true;

    (async () => {
      const Survey = await import('survey-react');
      await import('survey-react/survey.css');

      if (!config) return;
      const model = new Survey.Model(config);

      if (data) {
        try {
          const parsedData = typeof data === 'string' ? JSON.parse(data) : data;
          if (parsedData && typeof parsedData === 'object') {
            model.data = parsedData;
          }
        } catch (e) {}
      }

      if (preview) {
        model.deleteCookie();
        model.mode = 'display';
      }

      model.showNavigationButtons = false;

      const usedLocales = model.getUsedLocales();

      const localeOptions = usedLocales.map((code) => ({
        value: code,
        label: Survey.surveyLocalization.localeNames[code] || code
      }));

      const syncLocale = () => {
        if (!isMounted) return;
        const current = model.locale || Survey.surveyLocalization.defaultLocale;
        setActiveLocale(
          usedLocales.includes(current) ? current : usedLocales[0]
        );
      };

      model.onLocaleChangedEvent.add(syncLocale);

      if (usedLocales.includes(initialLocale)) {
        model.locale = initialLocale;
      }

      model.completedHtml =
        customHTML ||
        ReactDOMServer.renderToStaticMarkup(
          alternativeView ? <AlternativeCompletedPage /> : <CompletedPage />
        );

      const syncCompleted = () => {
        if (!isMounted) return;
        setCompleted(isSurveyCompleted(model));
      };

      model.onComplete.add(async (sender) => {
        await onComplete(sender);
        syncCompleted();
      });

      if (model.onStateChanged) {
        model.onStateChanged.add(() => {
          syncCompleted();
        });
      }

      syncCompleted();

      model.onAfterRenderQuestion.add(function (survey) {
        syncState(survey);
        syncCompleted();

        if (document.querySelector('.sv_q_radiogroup_control_item'))
          Array.prototype.forEach.call(
            document.querySelectorAll('.sv_q_radiogroup_control_item'),
            (item) => {
              const span = document.createElement('span');
              span.innerHTML =
                '<span class="checkmark-content">&#10003;</span>';
              span.className = 'checkmark';
              item.after(span);
            }
          );

        survey.onTextMarkdown.add(function (survey, options) {
          let str = converter.makeHtml(options.text);
          str = str.substring(3);
          str = str.substring(0, str.length - 4);
          options.html = str;
        });

        Array.prototype.forEach.call(
          document.querySelectorAll('.sv_q_checkbox_control_item'),
          (item) => {
            const span = document.createElement('span');
            span.innerHTML = '<span class="checkmark-content">&#10003;</span>';
            span.className = 'checkmark';
            item.after(span);
          }
        );

        const previewForm = document.querySelector('.surveyForm-preview');
        if (previewForm) {
          const isCustomNavButton = (el) =>
            el.closest('.surveyForm-navigation-bar') ||
            el.classList.contains('surveyForm-prev-page-button') ||
            el.classList.contains('back-btn');

          Array.prototype.forEach.call(
            previewForm.querySelectorAll(
              '.sv_main input, .sv_main select, .sv_main textarea, .sv_main button'
            ),
            (el) => {
              if (isCustomNavButton(el)) return;

              el.onclick = (e) => {
                e.preventDefault();
                e.stopPropagation();
                showMessagePopup({
                  message: 'Dit is een preview versie van het formulier',
                  buttonLabel: 'Terug'
                });
              };
            }
          );
        }
      });

      if (form?.xuid) {
        prefillSurveyForm(form.xuid, model, user);
        syncCompleted();
      }

      if (isMounted) {
        setModel(model);
        setSurvey(Survey);
        setLocales(localeOptions);
        syncLocale();
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [config]);

  const hideCustomNav = completed || isSurveyCompleted(model);

  return (
    <div
      className={getString([
        { value: 'surveyForm', condition: true },
        { value: 'surveyForm-preview', condition: preview },
        { value: 'surveyForm-alternative-view', condition: alternativeView }
      ])}
    >
      {preview && (
        <p className="surveyForm-preview-text">
          Dit is een preview versie van het formulier
        </p>
      )}

      {!hideCustomNav && locales.length > 1 && (
        <div className="surveyForm-language-selector-wrap">
          <select
            aria-label="Taal"
            value={activeLocale}
            className="surveyForm-language-selector"
            onChange={(e) => (model.locale = e.target.value)}
          >
            {locales.map(({ value, label }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
          <ChevronDown className="surveyForm-language-selector-icon" />
        </div>
      )}

      <ChevronLeft
        onClick={() => {
          if (!model) return;
          if (model.currentPageNo === 0) {
            router.push(returnTo || '/dashboard');
          } else model.prevPage();
        }}
        className="surveyForm-prev-page-button"
      />

      <p className="surveyForm-progress-bar">
        <span className="surveyForm-current-page-number">{currentPage}</span>/
        {pageCount}
      </p>

      {completed && customHTML && (
        <button
          className="back-btn"
          onClick={() => (location.href = returnTo || '/dashboard')}
        >
          <ChevronLeft className="back-btn-icon" />
          Terug
        </button>
      )}

      {model && Survey && (
        <Survey.Survey
          model={model}
          onCurrentPageChanged={handlePageChanged}
          onValueChanged={() => setIsLastPage(model.isLastPage)}
        />
      )}

      {!hideCustomNav && (
        <div className="surveyForm-navigation-bar">
          {isLastPage ? (
            <button
              className="surveyForm-button surveyForm-complete-button"
              onClick={() => {
                if (!model) return;

                if (preview) {
                  model.completeLastPage();
                  return;
                }

                model.completeLastPage();
              }}
            >
              Verzenden
            </button>
          ) : (
            <button
              className="surveyForm-button surveyForm-next-page-button"
              onClick={() => {
                if (!model) return;
                model.nextPage();
              }}
            >
              Volgende
            </button>
          )}
        </div>
      )}
    </div>
  );

  function syncState(survey) {
    setIsLastPage(survey.isLastPage);
    setCurrentPage(survey.currentPageNo + 1);
  }

  function handlePageChanged(survey) {
    syncState(survey);

    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
  }
}

function CompletedPage() {
  return (
    <div>
      <div className="surveyForm-check-icon-wrap">
        <Check className="surveyForm-check-icon" />
      </div>

      <h3 className="surveyForm-confirmation-title">
        Bedankt voor je antwoorden
      </h3>

      <p className="surveyForm-confirmation-text">
        Je kunt nu terugkeren naar de homepage of het dashboard.
      </p>

      <a href="/" className="surveyForm-form-button">
        Homepage
      </a>

      <a href="/dashboard" className="surveyForm-form-button">
        Dashboard
      </a>
    </div>
  );
}

function AlternativeCompletedPage() {
  return (
    <div>
      <div className="surveyForm-check-icon-wrap">
        <Check className="surveyForm-check-icon" />
      </div>

      <h3 className="surveyForm-confirmation-title">
        Bedankt voor je aanvraag
      </h3>

      <p className="surveyForm-confirmation-text">
        Op dit moment testen we de interesse in het huren van de
        babyuitzetlijst. We zullen je op de hoogte brengen als deze functie
        beschikbaar is.
      </p>

      <a
        href="/duurzaam/babyuitzetlijst"
        className="surveyForm-form-button surveyForm-form-button-alternative"
      >
        Naar duurzaamheid
      </a>
    </div>
  );
}

function getPageCount(model) {
  if (!model) return;
  if (model.visiblePageCount < model.PageCount)
    return model.visiblePageCount + 1;
  return model.PageCount;
}
